import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { EDigestPreference } from '@/graphql-types/globalTypes';
import { useNextGroupPriority } from '@/hooks/useNextGroupPriority';
import { usePermissions } from '@/hooks/usePermissions';
import { InfoPopup } from '@/imports/ui/components/InfoPopup';
import { getDigestLabel } from '@/utilities';

const orderTooltip = `This determines the order of your groups. If you choose a number that is already taken, the order of the previous group and the groups that follow it will automatically shift down.`;

type IDetailsCardForm = {
  name: string;
  description?: string;
  priority: number;
  defaultDigestPref: EDigestPreference;
};

export const DetailsForm: React.FC<{
  isGlobalSingleton: boolean;
  isEditing: boolean;
  parentGroupId?: string;
  isNewGroup: boolean;
}> = ({ isGlobalSingleton, isEditing, parentGroupId, isNewGroup }) => {
  const permissions = usePermissions();
  const { register, formState, watch } = useFormContext<IDetailsCardForm>();

  const { nextGroupPriority, loading: nextGroupPriorityLoading } =
    useNextGroupPriority({ parentGroupId });

  if (nextGroupPriorityLoading) {
    return null;
  }

  const orderOptions = [
    ...Array(isNewGroup ? nextGroupPriority + 1 : nextGroupPriority).keys(),
  ].map((val) => {
    return {
      key: String(val),
      value: val,
      text: String(val + 1),
    };
  });

  const name = watch('name');
  const description = watch('description');
  const defaultDigestPref = watch('defaultDigestPref');
  const priority = watch('priority');

  return (
    <VStack w="full" spacing={isEditing ? '4' : '8'}>
      <FormControl isRequired isInvalid={!!formState.errors.name}>
        <FormLabel>Name</FormLabel>
        {isEditing ? (
          <Input
            autoFocus
            placeholder="Enter a name for this group"
            disabled={isGlobalSingleton}
            {...register('name', { required: 'This is a required field' })}
          />
        ) : (
          <Text color="gray.600">{name}</Text>
        )}
        <FormErrorMessage>{formState.errors.name?.message}</FormErrorMessage>
      </FormControl>
      <FormControl>
        <FormLabel>Description</FormLabel>
        {isEditing ? (
          <Controller
            name="description"
            render={({ field: { onChange, value, ref } }) => (
              <Textarea
                value={value}
                onChange={onChange}
                ref={ref}
                aria-label="Group description"
                placeholder="Enter a description..."
              />
            )}
          />
        ) : (
          <Text color="gray.600" whiteSpace="pre-wrap">
            {!!description && description?.length > 0
              ? description
              : 'No description'}
          </Text>
        )}
      </FormControl>
      <FormControl>
        <FormLabel>Default Digest Preference</FormLabel>
        {isEditing ? (
          <Select {...register('defaultDigestPref')}>
            <option value={EDigestPreference.OFF}>
              {getDigestLabel(EDigestPreference.OFF)}
            </option>
            <option value={EDigestPreference.REALTIME}>
              {getDigestLabel(EDigestPreference.REALTIME)}
            </option>
            <option value={EDigestPreference.DAILY}>
              {getDigestLabel(EDigestPreference.DAILY)}
            </option>
            <option value={EDigestPreference.WEEKLY}>
              {getDigestLabel(EDigestPreference.WEEKLY)}
            </option>
          </Select>
        ) : (
          <Text color="gray.600">{getDigestLabel(defaultDigestPref)}</Text>
        )}
      </FormControl>
      {permissions.canDoAdminStuffAndAuthenticated && (
        <FormControl isRequired isInvalid={!!formState.errors.priority}>
          <HStack>
            <FormLabel>Group Order</FormLabel>
            <InfoPopup info={orderTooltip} />
          </HStack>
          {isEditing ? (
            <Controller
              name="priority"
              rules={{
                required: 'This is a required field',
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    onChange(parseInt(e.target.value));
                  }}
                  value={value}
                  ref={ref}
                >
                  {orderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </Select>
              )}
            ></Controller>
          ) : (
            <Text color="gray.600">{priority + 1}</Text>
          )}
          <FormErrorMessage>
            {formState.errors.priority?.message}
          </FormErrorMessage>
        </FormControl>
      )}
    </VStack>
  );
};
